import { type QueryKey, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { type Board } from '@air/api/types';

import { isPrivateBoardKey } from '~/components/v2/hooks/queries/usePrivateBoard';
import { type ReactQueryPredicate } from '~/swr-hooks/utils';

/**
 * Refetches the board itself, which is what carries `directDescendantCount` and
 * `directDescendantBoardCount`. Nothing else refreshes those, so a mutation that changes what a board
 * holds has to say so here or the board goes on reporting the counts it had on page load.
 *
 * Deliberately narrower than `updateBoardStats`, which also revalidates the deprecated board-stats
 * query. The bottombar reads that one and the upload pipeline writes it optimistically, so a mutation
 * that only needs the counts refreshed must not go through `updateBoardStats`: the refetch it triggers
 * lands after the optimistic write and puts the pre-mutation total back until the server catches up.
 */
export const useInvalidatePrivateBoard = () => {
  const queryClient = useQueryClient();

  const invalidatePrivateBoard = useCallback(
    (boardId: Board['id']) => {
      const boardPredicate: ReactQueryPredicate = ({ queryKey }: { queryKey: QueryKey }) =>
        isPrivateBoardKey(queryKey) && queryKey[1].boardId === boardId;

      queryClient.invalidateQueries({ predicate: boardPredicate });
    },
    [queryClient],
  );

  return {
    invalidatePrivateBoard,
  };
};
